import { useEffect } from 'react';
import { useAtomValue, useSetAtom } from 'jotai';

import { RESULTS_VIEW_KEYS, type DatasetAtoms } from './atoms';

// Params that don't change facet counts: paging and sorting refetch results but
// not facets, so they're excluded from the key that drives the facets request.
const FACET_IRRELEVANT_KEYS = new Set([
  ...RESULTS_VIEW_KEYS,
  'response_config',
]);

/**
 * Stable key over the facet-affecting params (extent, filters, text).
 * @param {Record<string, unknown> | undefined} payload The applied search.
 * @return {string | null} The key; null before any search, so the effect idles.
 */
function facetRelevantKey(
  payload: Record<string, unknown> | undefined
): string | null {
  if (!payload) return null;
  const relevant = Object.keys(payload)
    .filter((k) => !FACET_IRRELEVANT_KEYS.has(k))
    .sort()
    .reduce<Record<string, unknown>>((acc, k) => {
      acc[k] = payload[k];
      return acc;
    }, {});
  return JSON.stringify(relevant);
}

/**
 * Keeps a dataset's committed facets in step with its applied search, off the
 * results critical path; a no-op for datasets whose facets ride the main search.
 * Map Query's page-wide atoms also clear their results on unmount
 * (`resetOnUnmount`); a dataset whose atoms live and die with its own mount
 * needs no reset.
 * @param {object} props The lifecycle's dataset.
 * @param {DatasetAtoms} props.atoms The dataset's atoms.
 * @param {boolean} [props.resetOnUnmount] Clear the results on unmount.
 * @return {null} Renders nothing.
 */
export function DatasetLifecycle({
  atoms,
  resetOnUnmount = false,
}: {
  atoms: DatasetAtoms;
  resetOnUnmount?: boolean;
}) {
  const resetSearchData = useSetAtom(atoms.resetSearchDataAtom);
  const currentPayload = useAtomValue(atoms.searchAtom).currentPayload;
  const refreshCommittedFacets = useSetAtom(atoms.refreshCommittedFacetsAtom);

  useEffect(() => {
    if (!resetOnUnmount) return undefined;
    return () => resetSearchData({ unsetCurrentPayload: true });
  }, [resetOnUnmount, resetSearchData]);

  const facetKey = facetRelevantKey(currentPayload);
  useEffect(() => {
    if (facetKey === null) return;
    refreshCommittedFacets();
  }, [facetKey, refreshCommittedFacets]);

  return null;
}
